import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { ContractStatus } from "@/lib/types/contracts";
import { TestSubmissionTrashButton } from "@/features/get-paid/components/TestSubmissionTrashButton";
import type { GetPaidT } from "@/features/get-paid/hooks/get-paid-context";

type TestItem = {
  id: string;
  userId?: string;
  userName?: string;
  email?: string;
  language?: string;
  status?: string;
  correctionStatus?: string;
  submittedAt?: string;
  createdAt?: string;
  totalScore?: number;
  title?: string;
  contract?: { status?: string };
  recordVersion?: number;
};

type ContractProgress = { sent: boolean; signed: boolean; finalValidated: boolean };

interface ModeratorTestCardProps {
  test: TestItem;
  t: GetPaidT;
  isPending: boolean;
  contractProgress: ContractProgress;
  onDetails: (test: TestItem) => void;
  onSendContract: (test: TestItem) => void;
  onFinalValidate: (test: TestItem) => void;
  sendingContractTestId: string | null;
  finalValidatingTestId: string | null;
  isSuperAdmin: boolean;
  onTrash: (test: TestItem, reason: string) => Promise<boolean>;
}

function getStatusMeta(test: TestItem, contractStatus: string) {
  const cs = String(test?.correctionStatus || '').toUpperCase();
  const s = String(test?.status || '').toUpperCase();
  const isValidated = s === 'VALIDATED';
  const isPending = s === 'PENDING' && cs !== 'CORRECTED' && !isValidated;
  const isCorrectedAwaiting = cs === 'CORRECTED' && !isValidated;

  if (isPending) return { dot: "bg-primary", labelKey: "toCorrect", labelClass: "text-brand" };
  if (contractStatus === ContractStatus.DRAFT) return { dot: "bg-primary", labelKey: "contractToSend", labelClass: "text-brand" };
  if (contractStatus === ContractStatus.SENT) return { dot: "bg-primary", labelKey: "contractSentLabel", labelClass: "text-brand" };
  if (contractStatus === ContractStatus.VIEWED) return { dot: "bg-info", labelKey: "contractViewed", labelClass: "text-info" };
  if (contractStatus === ContractStatus.SIGNED) return { dot: "bg-info", labelKey: "readyForFinalValidation", labelClass: "text-info" };
  if (contractStatus === ContractStatus.FINAL_VALIDATED) return { dot: "bg-success", labelKey: "alreadyFinalized", labelClass: "text-success" };
  if (isCorrectedAwaiting) return { dot: "bg-info", labelKey: "corrected", labelClass: "text-info" };
  return { dot: "bg-success", labelKey: "validated", labelClass: "text-success" };
}

export function ModeratorTestCard({
  test, t, isPending, contractProgress,
  onDetails, onSendContract, onFinalValidate,
  sendingContractTestId, finalValidatingTestId,
  isSuperAdmin, onTrash,
}: ModeratorTestCardProps) {
  const status = String(test?.status || '').toUpperCase();
  const corrStatus = String(test?.correctionStatus || '').toUpperCase();
  const contractStatus = String(test?.contract?.status || '').trim().toLowerCase() as ContractStatus;
  const isValidated = status === 'VALIDATED';
  const isCorrected = corrStatus === 'CORRECTED' || isValidated;
  const isCorrectedAwaiting = corrStatus === 'CORRECTED' && !isValidated;
  const totalScore = test?.totalScore;
  const submittedAt = test?.submittedAt || test?.createdAt;
  const statusMeta = getStatusMeta(test, contractStatus);

  const canSendContract = isCorrectedAwaiting
    && typeof totalScore === 'number' && totalScore > 5
    && (!contractStatus || contractStatus === ContractStatus.DRAFT)
    && !contractProgress.sent;
  const canFinalValidate = isCorrectedAwaiting
    && contractStatus === ContractStatus.SIGNED
    && contractProgress.signed;
  const isReadonlyFinalized = contractStatus === ContractStatus.FINAL_VALIDATED;

  return (
    <article className="group border-b border-border/40 py-4 last:border-b-0">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", statusMeta.dot)} />
            <h3 className="truncate text-base font-semibold tracking-[-0.025em] text-foreground">
              {test?.userName || test.title}
            </h3>
            <span className={cn("text-[0.62rem] font-semibold uppercase tracking-[0.18em]", statusMeta.labelClass)}>
              {t.getPaid[statusMeta.labelKey]}
            </span>
            <span className="rounded-full border border-brand/18 bg-brand/[0.08] px-2.5 py-0.5 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-brand">
              {test.language}
            </span>
            {typeof totalScore === "number" && (
              <span className={cn(
                "text-xs font-medium leading-5",
                totalScore >= 7
                  ? "text-success"
                  : "text-danger"
              )}>
                {totalScore}/10
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground/64">
            {test?.email && <span>{test.email} <span className="text-foreground/14">/</span> </span>}
            {submittedAt && new Date(submittedAt).toLocaleString()}
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2">
          {status === 'PENDING' && !isCorrected && (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand">
              <Icon name="alertTriangle" className="h-3.5 w-3.5" />
              {t.getPaid.toCorrect}
            </span>
          )}
          {isPending && (
            <button type="button" onClick={() => onDetails(test)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400 transition-colors hover:text-slate-800 dark:hover:text-slate-200">
              <Icon name="eye" className="h-3.5 w-3.5" />
              {t.getPaid.details}
            </button>
          )}
          {!isPending && canSendContract && (
            <button type="button" onClick={() => onSendContract(test)}
              disabled={sendingContractTestId === String(test.id)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand transition-colors hover:text-brand disabled:cursor-not-allowed disabled:opacity-35">
              <Icon name="send" className="h-3.5 w-3.5" />
              {sendingContractTestId === String(test.id) ? t.getPaid.sending : t.getPaid.sendContract}
            </button>
          )}
          {!isPending && canFinalValidate && (
            <button type="button" onClick={() => onFinalValidate(test)}
              disabled={finalValidatingTestId === String(test.id)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-success transition-colors hover:text-success disabled:cursor-not-allowed disabled:opacity-35">
              <Icon name="checkCircle" className="h-3.5 w-3.5" />
              {finalValidatingTestId === String(test.id) ? t.getPaid.validating : t.getPaid.permanentlyValidate}
            </button>
          )}
          {!isPending && (contractStatus === ContractStatus.SENT || contractStatus === ContractStatus.VIEWED) && (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground/58">
              <Icon name="clock" className="h-3.5 w-3.5" />
              {contractStatus === ContractStatus.SENT ? t.getPaid.contractSentLabel : t.getPaid.contractViewed}
            </span>
          )}
          {!isPending && isReadonlyFinalized && (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground/58">
              <Icon name="lock" className="h-3.5 w-3.5" />
              {t.getPaid.readOnly}
            </span>
          )}
          {isSuperAdmin && (
            <TestSubmissionTrashButton
              test={test}
              t={t}
              onConfirm={onTrash}
            />
          )}
        </div>
      </div>
    </article>
  );
}

export type { TestItem as ModeratorTestItem };
